import { ALL_COLLECTIONS_OFF } from './wire';
/** The Index's facts.
 *
 *  The design's Index is not a menu of descriptions — every row carries a live
 *  fact: `02 AUG · 4 open`, `W31 · 7 habits`, `AUG · 4 in inbox`, `14 titles`,
 *  `AUG · 2/31`, `3 · 1 · 3`. That is what makes it a table of contents for
 *  *this* journal rather than a list of features, so the counts have to be real.
 *
 *  Every read is optional. A disabled collection, an unconfigured calendar or a
 *  missing subsystem answers 404, 409 or 503, and a row simply reports nothing
 *  rather than the whole page failing.
 */

import { request, type Session } from './client';
import { monthOf } from './month';
import { weekOf, isoWeek } from './week';
import type { MetadataKind } from './collections';
import type { WireCapabilities } from './wire';

/** One busy filter, for the rail's `FILTERS` section.
 *
 *  The sigil is part of the name in this product's vocabulary — `/Product`,
 *  `#KovackLaunch`, `@laptop` — so it is carried here rather than reconstructed
 *  by each caller.
 */
export interface BusyFilter {
  kind: MetadataKind;
  name: string;
  token: string;
  openTasks: number;
}

const SIGIL: Record<MetadataKind, string> = {
  streams: '/',
  projects: '#',
  contexts: '@',
};

/** How many filters the rail shows before it is a list rather than a shortcut.
 *
 *  Four, not more: the frame lists two, and the rail has to fit the shortest
 *  viewport that gets one. At 1024x768 — landscape tablet — four GO TO rows,
 *  five collections and four filters come to about 740px against 768 of height.
 *  Six pushed the last two below the fold, reachable only by scrolling a nav
 *  that gives no sign it scrolls.
 */
export const BUSY_FILTER_LIMIT = 4;

export interface IndexSummary {
  journalDate: string;
  openToday: number | null;
  habitCount: number | null;
  isoWeek: number;
  monthInbox: number | null;
  readingTitles: number | null;
  gratitudeWritten: number | null;
  gratitudeDays: number;
  streams: number | null;
  projects: number | null;
  contexts: number | null;
  /** The busiest few, sigil and open count included, for the rail. Derived from
   *  the same three responses the totals above come from — the metadata lists
   *  already carry `active_task_count`, so this costs no extra request. */
  busiest: BusyFilter[];
  calendars: number | null;
  /** Where this client is pointed, for the subtitle. */
  server: string;
  syncedAt: string;
}

async function count(session: Session, path: string): Promise<number | null> {
  const items = await list(session, path);
  return items === null ? null : items.length;
}

interface MetadataItem {
  name?: unknown;
  active_task_count?: unknown;
}

/** The items themselves, or null when the read is not available. */
async function list(session: Session, path: string): Promise<MetadataItem[] | null> {
  try {
    const body = (await request<{ items: unknown[] }>(session, path)).body;
    return Array.isArray(body.items) ? (body.items as MetadataItem[]) : null;
  } catch {
    return null;
  }
}

/** The busiest filters across all three kinds, by open task count.
 *
 *  Only ones with open work are listed: a stream with nothing in it is not a
 *  shortcut worth a row, and the rail has to stay a rail.
 */
function busiestOf(
  found: { kind: MetadataKind; items: MetadataItem[] | null }[],
): BusyFilter[] {
  return found
    .flatMap(({ kind, items }) =>
      (items ?? []).flatMap((item) => {
        const name = typeof item.name === 'string' ? item.name : null;
        const openTasks =
          typeof item.active_task_count === 'number' ? item.active_task_count : 0;
        if (!name || openTasks <= 0) return [];
        return [{ kind, name, token: `${SIGIL[kind]}${name}`, openTasks }];
      }),
    )
    // Count first, then name, so the order does not wobble between reads when
    // two filters are equally busy.
    .sort((a, b) => b.openTasks - a.openTasks || a.token.localeCompare(b.token))
    .slice(0, BUSY_FILTER_LIMIT);
}

export async function indexSummary(
  session: Session,
  journalDate: string,
  capabilities: WireCapabilities | null,
): Promise<IndexSummary> {
  const week = weekOf(journalDate);
  const month = monthOf(journalDate);
  const collections = capabilities?.collections ?? ALL_COLLECTIONS_OFF;

  const [day, habits, inbox, reading, gratitude, streams, projects, contexts, calendars] =
    await Promise.all([
      request<{ entries: { state: string }[] }>(session, `/v1/days/${journalDate}`)
        .then((r) => r.body.entries.filter((e) => e.state === 'open').length)
        .catch(() => null),
      collections['habits']
        ? count(session, `/v1/habits/tracker?from=${week.from}&to=${week.to}`)
        : Promise.resolve(null),
      count(session, `/v1/entries?kind=task&active_month=${month.month}&limit=100`),
      collections['reading_log']
        ? count(session, '/v1/collections/reading-log?limit=100')
        : Promise.resolve(null),
      collections['gratitude_log']
        ? count(session, `/v1/collections/gratitude-log?month=${month.month}`)
        : Promise.resolve(null),
      list(session, '/v1/streams?limit=100'),
      list(session, '/v1/projects?limit=100'),
      list(session, '/v1/contexts?limit=100'),
      request<{ connections: Record<string, unknown> }>(session, '/v1/calendar/connection')
        .then((r) => Object.keys(r.body.connections ?? {}).length)
        .catch(() => null),
    ]);

  return {
    journalDate,
    openToday: day,
    habitCount: habits,
    isoWeek: isoWeek(week.from),
    monthInbox: inbox,
    readingTitles: reading,
    gratitudeWritten: gratitude,
    gratitudeDays: month.days.length,
    streams: streams?.length ?? null,
    projects: projects?.length ?? null,
    contexts: contexts?.length ?? null,
    busiest: busiestOf([
      { kind: 'streams', items: streams },
      { kind: 'projects', items: projects },
      { kind: 'contexts', items: contexts },
    ]),
    calendars,
    // Empty base URL means same-origin, which is the deployed shape.
    server: session.baseUrl ? new URL(session.baseUrl).host : location.host,
    syncedAt: new Date().toTimeString().slice(0, 5),
  };
}
